import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiCheckCircle, FiAlertCircle, FiInfo, FiDollarSign, FiSmartphone } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const PaymentConfigCard = ({ onConfigured }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [method, setMethod] = useState('mercadopago');
  const [formData, setFormData] = useState({
    mp_access_token: '',
    pix_key: '',
    pix_key_type: 'cpf'
  });
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/stores/my/payment-config');
      setPaymentConfig(response.data);
      setMethod(response.data.payment_method || 'mercadopago');
      if (response.data.pix_key) {
        setFormData(prev => ({
          ...prev,
          pix_key: response.data.pix_key,
          pix_key_type: response.data.pix_key_type || 'cpf'
        }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { payment_method: method };
      
      if (method === 'mercadopago') {
        if (!formData.mp_access_token && !paymentConfig?.has_mp_token) {
          alert('Por favor, insira o Access Token do Mercado Pago');
          setSaving(false);
          return;
        }
        payload.mp_access_token = formData.mp_access_token || undefined;
      } else {
        if (!formData.pix_key) {
          alert('Por favor, insira sua chave PIX');
          setSaving(false);
          return;
        }
        payload.pix_key = formData.pix_key;
        payload.pix_key_type = formData.pix_key_type;
      }

      await api.put('/stores/my/payment-config', payload);
      alert('Configuração salva com sucesso!');
      loadConfig();
      onConfigured?.();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const isConfigured = method === 'mercadopago' 
    ? paymentConfig?.has_mp_token 
    : paymentConfig?.pix_key;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 p-4 text-white">
        <div className="flex items-center gap-3">
          <FiDollarSign className="text-2xl" />
          <div>
            <h3 className="font-semibold">Como você quer receber?</h3>
            <p className="text-sm text-green-100">Configure seu método de recebimento</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status */}
        <div className={`p-4 rounded-xl flex items-center gap-3 ${isConfigured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          {isConfigured ? (
            <>
              <FiCheckCircle className="text-2xl text-green-600" />
              <div>
                <p className="font-medium text-green-800">
                  {method === 'mercadopago' ? 'Mercado Pago Configurado' : 'PIX Configurado'}
                </p>
                <p className="text-sm text-green-600">
                  {method === 'mercadopago' 
                    ? 'Você recebe automaticamente a cada venda' 
                    : 'Você recebe repasse semanal (toda segunda-feira)'}
                </p>
              </div>
            </>
          ) : (
            <>
              <FiAlertCircle className="text-2xl text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Configuração Pendente</p>
                <p className="text-sm text-yellow-600">Configure para começar a receber</p>
              </div>
            </>
          )}
        </div>

        {/* Method Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">Método de Recebimento</label>
          
          {/* Mercado Pago Option */}
          <div 
            onClick={() => setMethod('mercadopago')}
            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
              method === 'mercadopago' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                method === 'mercadopago' ? 'border-blue-500' : 'border-gray-300'
              }`}>
                {method === 'mercadopago' && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FiCreditCard className="text-blue-600" />
                  <span className="font-medium">Mercado Pago</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Recomendado</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Receba instantaneamente a cada venda. Funciona com CPF (não precisa CNPJ).
                </p>
              </div>
            </div>
          </div>

          {/* PIX Platform Option */}
          <div 
            onClick={() => setMethod('pix_platform')}
            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
              method === 'pix_platform' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                method === 'pix_platform' ? 'border-green-500' : 'border-gray-300'
              }`}>
                {method === 'pix_platform' && <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FiSmartphone className="text-green-600" />
                  <span className="font-medium">Chave PIX</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Repasse semanal (toda segunda). Informe apenas sua chave PIX.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mercado Pago Config */}
        {method === 'mercadopago' && (
          <div className="space-y-3 pt-2">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <FiInfo className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Como obter o Access Token:</p>
                  <ol className="text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Acesse mercadopago.com.br</li>
                    <li>Vá em "Seu negócio" → "Configurações"</li>
                    <li>Clique em "Credenciais"</li>
                    <li>Copie o "Access Token" de produção</li>
                  </ol>
                  <p className="mt-2 text-blue-600">
                    <strong>Nota:</strong> Funciona com conta pessoal (CPF). Não precisa de CNPJ!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Access Token do Mercado Pago
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={formData.mp_access_token}
                  onChange={e => setFormData({...formData, mp_access_token: e.target.value})}
                  placeholder={paymentConfig?.has_mp_token ? '••••••••••••••••' : 'APP_USR-...'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-blue-600"
                >
                  {showToken ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              {paymentConfig?.has_mp_token && (
                <p className="text-xs text-gray-500">Deixe em branco para manter o token atual</p>
              )}
            </div>
          </div>
        )}

        {/* PIX Config */}
        {method === 'pix_platform' && (
          <div className="space-y-3 pt-2">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex gap-3">
                <FiInfo className="text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-800">Como funciona:</p>
                  <ul className="text-green-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Os pagamentos vão para a conta da plataforma</li>
                    <li>Todo segunda-feira fazemos o repasse via PIX</li>
                    <li>Comissão é descontada automaticamente</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tipo da Chave PIX</label>
              <select
                value={formData.pix_key_type}
                onChange={e => setFormData({...formData, pix_key_type: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none"
              >
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="random">Chave Aleatória</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Chave PIX</label>
              <input
                type="text"
                value={formData.pix_key}
                onChange={e => setFormData({...formData, pix_key: e.target.value})}
                placeholder={
                  formData.pix_key_type === 'cpf' ? '000.000.000-00' :
                  formData.pix_key_type === 'cnpj' ? '00.000.000/0000-00' :
                  formData.pix_key_type === 'email' ? 'seu@email.com' :
                  formData.pix_key_type === 'phone' ? '(11) 99999-9999' :
                  'Chave aleatória'
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-3 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 ${
            method === 'mercadopago' 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {saving ? 'Salvando...' : 'Salvar Configuração'}
        </button>
      </div>
    </div>
  );
};

export default PaymentConfigCard;
